import ODSCollapseDown from "@/atoms/ODSIcons/collapse-down/ODSCollapseDown";
import { ODSLogo } from "@/atoms/ODSLogo";
import { useViewportSize } from "@/utils";
import { Slot } from "@radix-ui/react-slot";
import cn from "classnames";
import React from "react";

import {
  ODSExtendedNavCategory,
  ODSExtendedNavProps,
  ODSFooterCategoryProps,
  ODSFooterLogoProps,
  ODSFooterMenuItemProps,
  ODSFooterMinimalProps,
  ODSFooterProps,
} from "./ODSFooter.types";

import "./css/index.css";

/*
  TODO (extra)
  ============
  - [ ] ~ ellipsis for long labels? (category + item)
*/

const ODSFooter = (props: ODSFooterProps) => {
  const {
    className,
    children,
    variant = "standard",
    extendedNavItems = [],
    logoProps,
    socialLabel,
    socialIcons,
    slotElement,
    contentSlot,
    copyrightText,
    ...restProps
  } = props;
  const viewport = useViewportSize();

  // If children is not what we expect by default, we don't render the default layout.
  // By default this expects an array of ODSFooterMenuItem elements.
  // For custom layouts to work properly we would need to also export almost all components below.
  // (We're testing this idea.)
  const childrenIsCustom = React.Children.toArray(children).some(
    (child) => !React.isValidElement(child) || child.type !== ODSFooterMenuItem
  );

  return (
    <footer
      role="contentinfo"
      className={cn("ODSFooter", `ODSFooter--variant-${variant}`, className)}
      {...restProps}
      data-scheme="black"
    >
      {childrenIsCustom ? (
        children
      ) : (
        <>
          {variant === "extended" ? (
            <ODSExtendedNav items={extendedNavItems} viewport={viewport} />
          ) : null}

          {variant === "minimal" ? null : (
            <ODSFooterLogo
              logoProps={logoProps}
              socialLabel={socialLabel}
              socialIcons={socialIcons}
              slotElement={slotElement}
              contentSlot={contentSlot}
            />
          )}
          <ODSFooterMinimal copyrightText={copyrightText}>
            {children}
          </ODSFooterMinimal>
        </>
      )}
    </footer>
  );
};
ODSFooter.displayName = "ODSFooter";

export const ODSExtendedNav = ({
  items,
  viewport = "large",
  ...props
}: ODSExtendedNavProps) => {
  const category = (item: ODSExtendedNavCategory, i: number) => (
    <ODSFooterCategory
      key={item.id ?? i}
      text={item.text ?? item.label}
      viewport={viewport}
    >
      {item.content}
    </ODSFooterCategory>
  );

  // Below we divide `items` into two columns for "medium" viewports.
  // This is unoptimal, using JS for layout, but it's needed for the masonry-like layout.
  // Using flex or grid would be better (no masonry for now).

  return (
    <nav className="ODSExtendedNav-Wrapper" data-scheme="white" {...props}>
      <div className={cn("ODSExtendedNav", "ODSFooter-Container")}>
        {viewport === "medium" ? (
          <>
            <div data-column="1">
              {items.slice(0, items.length / 2).map(category)}
            </div>
            <div data-column="2">
              {items.slice(items.length / 2, items.length).map(category)}
            </div>
          </>
        ) : (
          items.map(category)
        )}
      </div>
    </nav>
  );
};
ODSExtendedNav.displayName = "ODSExtendedNav";

const ODSFooterLogo = (props: ODSFooterLogoProps) => {
  const { socialLabel, socialIcons, slotElement, contentSlot, logoProps } =
    props;
  const _contentSlot = contentSlot ?? slotElement;
  const hasSocial = socialIcons != null;
  const hasSlot = _contentSlot != null && !hasSocial; // TODO ask about this

  return (
    <div
      className={cn("ODSFooterLogo", "ODSFooter-Container", {
        "ODSFooterLogo--has-content": hasSocial || hasSlot,
        "ODSFooterLogo--has-social": hasSocial,
        "ODSFooterLogo--has-slot": hasSlot,
      })}
    >
      <div className="ODSFooterLogo-LogoContainer">
        <ODSLogo type="secondary" {...logoProps} />
      </div>
      {hasSocial && (
        <div className="ODSFooterLogo-Social">
          {socialLabel && (
            <h2 className="ODSFooterLogo-SocialLabel">{socialLabel}</h2>
          )}
          <div className="ODSFooterLogo-SocialIcons">{socialIcons}</div>
        </div>
      )}
      {hasSlot && <div className="ODSFooterLogo-Slot">{_contentSlot}</div>}
    </div>
  );
};
ODSFooterLogo.displayName = "ODSFooterLogo";

const ODSFooterMinimal = (props: ODSFooterMinimalProps) => {
  const { copyrightText = "© Deutsche Telekom AG", children } = props;
  return (
    <div className={cn("ODSFooterMinimal", "ODSFooter-Container")}>
      {copyrightText && (
        <span className="ODSFooterMinimal-Copyright">{copyrightText}</span>
      )}
      <div className="ODSFooter-Links">{children}</div>
    </div>
  );
};
ODSFooterMinimal.displayName = "ODSFooterMinimal";

const ODSFooterCategory = ({
  text,
  viewport = "large",
  children,
}: ODSFooterCategoryProps) => {
  if (viewport === "small") {
    return (
      <ODSFooterCategorySmall text={text} viewport={viewport}>
        {children}
      </ODSFooterCategorySmall>
    );
  }

  return (
    <div className="ODSFooterCategory">
      <h2 className="ODSFooterCategory-Label">{text}</h2>
      <div className="ODSFooterCategory-Content">{children}</div>
    </div>
  );
};
ODSFooterCategory.displayName = "ODSFooterCategory";

const ODSFooterCategorySmall = (props: ODSFooterCategoryProps) => {
  const { text, children } = props;
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="ODSFooterCategory">
      <h2 className="ODSFooterCategory-LabelWrapper">
        <button
          className="ODSFooterCategory-Label"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {text}
          <ODSCollapseDown
            className="ODSFooterCategory-LabelIcon"
            size={16}
            decorative
          />
        </button>
      </h2>
      <div className="ODSFooterCategory-Content" hidden={!expanded}>
        {children}
      </div>
    </div>
  );
};
ODSFooterCategorySmall.displayName = "ODSFooterCategorySmall";

const ODSFooterMenuItem = React.forwardRef<
  HTMLAnchorElement,
  React.HTMLProps<HTMLAnchorElement> & ODSFooterMenuItemProps
>((props, ref) => {
  const { asChild, text, label, children, ...rest } = props;
  const Comp = asChild ? Slot : "a";
  return (
    <Comp ref={ref} className="ODSFooterMenuItem" {...rest}>
      {asChild ? children : <span>{text ?? label}</span>}
    </Comp>
  );
});
ODSFooterMenuItem.displayName = "ODSFooterMenuItem";

export { ODSFooter, ODSFooterMenuItem };
